import { useTranslation } from 'react-i18next';
import { IconPaperclip } from '@tabler/icons-react';
import TeeBadge from '@/components/ui/tee-badge';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeContentThumbnail } from '@/components/ui/tee-content-thumbnail';
import { ContentItem } from '@/types/contentItem';
import { MEDIA_TYPE_ICON } from '@/utils/contentMediaType';

// Cap visible badges at 3 with a "+N" overflow badge, per D-14/UI-SPEC.
const MAX_VISIBLE_BADGES = 3;

export interface ResourceFinderCardProps {
  item: ContentItem;
  categoryLabels: string[];
  archetypeLabels: string[];
  onPreview?: (item: ContentItem) => void;
  onAttach: (item: ContentItem) => void;
  attachLabel: string;
  attachDisabled?: boolean;
  disabled?: boolean;
}

export function ResourceFinderCard({
  item,
  categoryLabels,
  archetypeLabels,
  onPreview,
  onAttach,
  attachLabel,
  attachDisabled = false,
  disabled = false,
}: ResourceFinderCardProps) {
  const { t } = useTranslation();

  const thumbnailUrl = item.videoThumbnailUrl ?? item.imageSignedUrl ?? item.imageUrl;
  const MediaIcon = MEDIA_TYPE_ICON[item.mediaType];

  const badgeLabels = [...categoryLabels, ...archetypeLabels];
  const visibleBadges = badgeLabels.slice(0, MAX_VISIBLE_BADGES);
  const overflowCount = badgeLabels.length - visibleBadges.length;

  const handleAttachClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onAttach(item);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onPreview) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPreview(item);
    }
  };

  const previewTriggerProps = onPreview
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': t('contentLibrary.previewAriaLabel', { title: item.title }),
        onClick: () => onPreview(item),
        onKeyDown: handleKeyDown,
      }
    : {};

  return (
    <div
      className={`group flex flex-col gap-2 rounded-[8px] border border-[var(--border-default)] bg-[var(--background-card)] p-4 transition-all duration-150 hover:border-[var(--border-active)] hover:shadow-sm hover:-translate-y-[1px]${onPreview ? ' cursor-pointer' : ''}`}
      {...previewTriggerProps}
    >
      <TeeContentThumbnail
        title={item.title}
        mediaType={item.mediaType}
        thumbnailUrl={thumbnailUrl}
        className="aspect-video w-full"
        iconSize={32}
        previewable={!!onPreview}
      />

      <div className="flex items-start gap-1.5">
        <MediaIcon size={16} className="mt-0.5 shrink-0 text-[var(--text-content-sub-title)]" />
        <p className="line-clamp-2 text-[16px] font-semibold text-[var(--text-content-title)]">
          {item.title}
        </p>
      </div>

      {badgeLabels.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {visibleBadges.map((label, index) => (
            <TeeBadge key={`${label}-${index}`} label={label} size="s" type="grey" />
          ))}
          {overflowCount > 0 && <TeeBadge label={`+${overflowCount}`} size="s" type="grey" />}
        </div>
      )}

      <TeeButton
        variant="secondary"
        size="s"
        className="mt-auto"
        leftIcon={<IconPaperclip size={14} />}
        text={attachLabel}
        disabled={disabled || attachDisabled}
        aria-label={t('contentLibrary.attachAriaLabel', { title: item.title })}
        onClick={handleAttachClick}
      />
    </div>
  );
}
